// =============================================================================
// Breadcrumb trail (frontend → /api/log/frontend-error)
//
// Bounded in-memory ring buffer of recent user/system actions. Attached to
// every error event so the Kibana row reads like:
//
//   12:04:11.882  nav    /trade/dashboard
//   12:04:12.501  ui     click [data-track="open-order-form"]
//   12:04:12.512  http   GET /api/positions → 200 (47ms)
//   12:04:14.220  ui     click [data-track="place-order-submit"]
//   12:04:14.231  http   POST /api/orders → 500 (812ms)
//   12:04:14.244  ERROR  TypeError: Cannot read property 'order_id' of undefined
//
// Design rules:
//   • Strict cap (BREADCRUMB_MAX) — runaway pages can never grow this past the
//     payload budget (~5 KB on the wire at the cap).
//   • All values truncated at write time so the buffer is constant-memory.
//   • No persistence — breadcrumbs are session-scoped and die with the tab.
//     (recent-routes mirrors to sessionStorage; breadcrumbs do NOT, because
//     a reload mid-flow loses interactivity context anyway.)
//   • No timer, no I/O, no listeners — this module is pure data.
//
// Privacy: callers must never pass form values, financial amounts, PINs, or
// tokens. The category constants below match the four allow-listed crumb
// sources; anything outside these uses cat="custom" and goes through stricter
// scrubbing on the backend.
// =============================================================================

export type BreadcrumbCategory = "nav" | "http" | "ui" | "auth" | "tab" | "custom"

export interface Breadcrumb {
  /** ms since epoch — kept numeric to minimize payload size */
  ts: number
  cat: BreadcrumbCategory
  msg: string
  data?: Record<string, string | number | boolean>
}

const BREADCRUMB_MAX = 50
const MSG_MAX = 200
const DATA_KEY_MAX = 6
const DATA_VAL_MAX = 200

const buffer: Breadcrumb[] = []

function truncate(s: unknown, max: number): string {
  if (typeof s !== "string") return String(s ?? "").slice(0, max)
  return s.length > max ? s.slice(0, max) : s
}

function sanitizeData(
  data: Record<string, unknown> | undefined
): Record<string, string | number | boolean> | undefined {
  if (!data) return undefined
  const out: Record<string, string | number | boolean> = {}
  let count = 0
  for (const k of Object.keys(data)) {
    if (count >= DATA_KEY_MAX) break
    const v = data[k]
    if (v === null || v === undefined) continue
    if (typeof v === "number" || typeof v === "boolean") {
      out[k] = v
    } else if (typeof v === "string") {
      out[k] = truncate(v, DATA_VAL_MAX)
    } else {
      // Drop objects / arrays / functions / symbols. Stringifying them would
      // either explode ES mapping (each unique shape becomes a new sub-field)
      // or — worse — silently land as "[object Object]" everywhere.
      continue
    }
    count++
  }
  return count > 0 ? out : undefined
}

/**
 * Append a breadcrumb. O(1) amortized. Safe to call from any context —
 * never throws, never blocks.
 */
export function addBreadcrumb(
  cat: BreadcrumbCategory,
  msg: string,
  data?: Record<string, unknown>
): void {
  try {
    buffer.push({
      ts: Date.now(),
      cat,
      msg: truncate(msg, MSG_MAX),
      data: sanitizeData(data),
    })
    // Drop oldest first — most recent N crumbs are the most relevant to a
    // crash that just happened.
    while (buffer.length > BREADCRUMB_MAX) buffer.shift()
  } catch {
    // The breadcrumb path must never feed back into the reporter. Swallow.
  }
}

/**
 * Snapshot the current trail. Returned array is a defensive copy so the
 * caller can safely include it in JSON.stringify without us mutating it
 * mid-serialize on a subsequent push.
 */
export function getBreadcrumbs(): Breadcrumb[] {
  return buffer.slice()
}

/**
 * Wipe the trail. Called on session-expiry so a fresh login doesn't inherit
 * the previous user's interaction history (privacy + diagnostic noise).
 */
export function clearBreadcrumbs(): void {
  buffer.length = 0
}

// Test-only knob: exported for the unit test to assert the cap. Not part of
// the public contract; do NOT import in app code.
export const __BREADCRUMB_MAX_FOR_TESTS = BREADCRUMB_MAX
